/**
 * The room pins and their visibility (card `t_bca466e2`, plan §3.4 + §3.7): the
 * tri-state that decides which of them are drawn, where each one's anchor sits
 * and the page it opens. The pin itself is `BimRoomPin.vue` — a pill with the
 * room's picture and its name, a tap to bring the room into view and a long
 * press to open it (owner, 2026-10-14).
 *
 * The decisions here are the locked ones:
 *
 *   · **`Room labels: All / Current room / Off`**, part of the house's view
 *     settings on the hub since 2026-10-29 (it was per dashboard on this device
 *     before, under its own key — the helpers below are that older store, kept
 *     for their own tests; the page no longer calls them).
 *   · **Default by navigation mode**: orbit is a view of the whole house, so it
 *     opens on *All*; the first-person modes put the operator inside one room,
 *     where every other pin is a label in their face — they open on *Current
 *     room*, and "current" is the room the CAMERA stands in (`pointInRoom`).
 *   · **A stored choice wins over the default** — the default only decides what
 *     the surface opens on.
 *   · **Viewers get labels too** (§3.7): the pins are not edit-mode chrome. The
 *     VOLUMES are (see `useBimRoomsLayer`); the labels are how a viewer reads
 *     the rooms at all.
 */

import { pointInRoom, roomAreaOfRoom, type BimRoom } from '@/utils/bimRooms';

export type BimRoomLabelMode = 'all' | 'current' | 'off';

/** The tri-state in menu order. */
export const BIM_ROOM_LABEL_MODES: readonly BimRoomLabelMode[] = ['all', 'current', 'off'];

/** The app's navigation modes, as the label rule reads them. */
export type BimRoomLabelNavMode = 'orbit' | 'gravity' | 'sim';

/** The storage key prefix; the dashboard id follows it. */
export const BIM_ROOM_LABELS_STORAGE_PREFIX = 'bim-room-labels:';

export function roomLabelStorageKey(dashboardId: string): string {
  return `${BIM_ROOM_LABELS_STORAGE_PREFIX}${dashboardId}`;
}

/** A stored value, or `null` when nothing usable is stored. */
export function parseRoomLabelMode(raw: unknown): BimRoomLabelMode | null {
  return typeof raw === 'string' && (BIM_ROOM_LABEL_MODES as readonly string[]).includes(raw)
    ? (raw as BimRoomLabelMode)
    : null;
}

/** What the surface opens on when the operator has never chosen. */
export function defaultRoomLabelMode(navMode: BimRoomLabelNavMode): BimRoomLabelMode {
  return navMode === 'orbit' ? 'all' : 'current';
}

/** The stored choice, else the mode's own default. */
export function resolveRoomLabelMode(stored: BimRoomLabelMode | null, navMode: BimRoomLabelNavMode): BimRoomLabelMode {
  return stored ?? defaultRoomLabelMode(navMode);
}

export function readStoredRoomLabelMode(
  storage: Pick<Storage, 'getItem'> | null | undefined,
  dashboardId: string,
): BimRoomLabelMode | null {
  if (!storage || dashboardId.length === 0) return null;
  try {
    return parseRoomLabelMode(storage.getItem(roomLabelStorageKey(dashboardId)));
  } catch {
    return null;
  }
}

/**
 * The browser's own storage, or `null` on a surface that has none.
 *
 * This is the ONE place the label mode touches the device, and it lives here so
 * the LAYOUT never mentions it: the BIM page's own contract is that nothing a
 * user EDITS is kept on the device (`BimLayout.staticEdits.contract.test.ts` —
 * "the panel must not keep edits on the device"). A view setting is not an edit
 * and is stored per dashboard; element and room documents never leave the hub.
 */
export function roomLabelStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The mode stored for a dashboard on THIS device, or null. */
export function readRoomLabelMode(dashboardId: string): BimRoomLabelMode | null {
  return readStoredRoomLabelMode(roomLabelStorage(), dashboardId);
}

/** Remember the mode for a dashboard on this device. */
export function writeRoomLabelMode(mode: BimRoomLabelMode, dashboardId: string): void {
  storeRoomLabelMode(mode, roomLabelStorage(), dashboardId);
}

export function storeRoomLabelMode(
  mode: BimRoomLabelMode,
  storage: Pick<Storage, 'setItem'> | null | undefined,
  dashboardId: string,
): void {
  if (!storage || dashboardId.length === 0) return;
  try {
    storage.setItem(roomLabelStorageKey(dashboardId), mode);
  } catch {
    // A storage that refuses (private mode, a full quota) leaves the surface on
    // the mode it is already showing — the choice is not worth a broken page.
  }
}

/**
 * The rooms whose pins are drawn, in document order.
 *
 *   · `all`     — every room that is not hidden;
 *   · `current` — the room the camera is inside (`pointInRoom`, the same test
 *                 the pick uses); with no camera position there is no current
 *                 room and nothing is drawn;
 *   · `off`     — nothing.
 *
 * Rooms the operator has HIDDEN are out of every mode: a hidden room is one the
 * document says not to draw, and the pins are drawn from the document.
 */
export function visibleRoomLabels(
  rooms: readonly BimRoom[],
  input: { mode: BimRoomLabelMode; point: { x: number; y: number; z: number } | null },
): BimRoom[] {
  const shown = rooms.filter((room) => !room.hidden);
  if (input.mode === 'off') return [];
  if (input.mode === 'all') return shown;
  if (!input.point) return [];
  const current = shown
    .filter((room) => pointInRoom(room, input.point as { x: number; z: number; y?: number }))
    .sort((a, b) => roomAreaOfRoom(a) - roomAreaOfRoom(b));
  return current.length > 0 ? [current[0]] : [];
}

/**
 * Where a pin's anchor sits: the room's own centre on its floor, lifted to eye
 * height (or the room's own mid-height when it is shorter than that) — the point
 * the occlusion cast asks about and the point that is projected.
 */
export function roomPinAnchor(room: BimRoom, eyeHeightM = 1.5): { x: number; y: number; z: number } | null {
  const section = room.sections[0];
  if (!section) return null;
  let x = 0;
  let z = 0;
  for (const row of room.sections) {
    x += row.center[0];
    z += row.center[1];
  }
  x /= room.sections.length;
  z /= room.sections.length;
  const floor = Math.min(...room.sections.map((row) => row.y0));
  const ceiling = Math.max(...room.sections.map((row) => row.y1));
  const height = Math.max(0, ceiling - floor);
  return { x, y: floor + Math.min(eyeHeightM, Math.max(0.5, height * 0.75)), z };
}

/**
 * The page a pin's **Open room** gesture goes to — the device editor's own deep
 * link shape (`deviceRoomLink`): `/rooms/:id`, or `/h/<hubId>/rooms/:id` when
 * the layout is rendering inside a hub-scoped route.
 */
export function roomPageLink(roomId: string, hubId: string | null): string {
  return hubId ? `/h/${hubId}/rooms/${roomId}` : `/rooms/${roomId}`;
}
